import React, { type ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { BaseModal, type BaseModalFooterAction } from '../base'

export type ConfirmDialogIntent = 'error' | 'primary' | 'warning' | 'success'

export type ConfirmDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  loading?: boolean
  /** Tiêu đề dialog (VD: "Xóa nhà cung cấp"). */
  title: ReactNode
  message?: ReactNode
  template?: {
    prefix?: ReactNode
    codes: Array<string | number | null | undefined>
    suffix?: ReactNode
    moreText?: ReactNode
    maxInlineCodes?: number
  }
  children?: ReactNode
  intent?: ConfirmDialogIntent
  confirmText?: ReactNode
  cancelText?: ReactNode
  closeButtonAriaLabel?: string
  showCancel?: boolean
  closeOnConfirm?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  disableBackdropClose?: boolean
  centered?: boolean
}

const intentToColor: Record<ConfirmDialogIntent, 'error' | 'primary' | 'warning' | 'success'> = {
  error: 'error',
  primary: 'primary',
  warning: 'warning',
  success: 'success',
}

/**
 * Dialog xác nhận — port từ banhang `ConfirmDialog`, dựng trên `BaseModal`.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  title,
  message,
  template,
  children,
  intent = 'primary',
  confirmText = 'Xác nhận',
  cancelText = 'Hủy',
  closeButtonAriaLabel = 'Đóng',
  showCancel = true,
  closeOnConfirm = true,
  maxWidth = 'sm',
  disableBackdropClose = false,
  centered = false,
}) => {
  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleConfirm = () => {
    if (loading) return
    onConfirm()
    if (closeOnConfirm) onClose()
  }

  const isCloseDisabled = loading || disableBackdropClose

  const footerActions: BaseModalFooterAction[] = []
  if (showCancel) {
    footerActions.push({
      id: 'cancel',
      label: cancelText,
      variant: 'outlined',
      color: 'inherit',
      onClick: handleClose,
      disabled: loading,
      startIcon: <CloseOutlinedIcon sx={{ fontSize: 20 }} />,
    })
  }
  footerActions.push({
    id: 'confirm',
    label: confirmText,
    variant: 'contained',
    color: intentToColor[intent],
    onClick: handleConfirm,
    disabled: loading,
    startIcon:
      intent === 'error' ? (
        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
      ) : (
        <CheckIcon sx={{ fontSize: 20 }} />
      ),
  })

  return (
    <BaseModal
      open={open}
      onClose={isCloseDisabled ? () => {} : onClose}
      title={title}
      maxWidth={maxWidth}
      centered={centered}
      closeButtonAriaLabel={closeButtonAriaLabel}
      footerActions={footerActions}
    >
      <Box sx={{ pt: 1, pb: 0.5 }}>
        {children ? (
          children
        ) : message != null ? (
          <Typography
            component="div"
            sx={{ fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.6 }}
          >
            {message}
          </Typography>
        ) : template ? (
          <TemplateMessage template={template} />
        ) : null}
      </Box>
    </BaseModal>
  )
}

const TemplateMessage: React.FC<{
  template: NonNullable<ConfirmDialogProps['template']>
}> = ({ template }) => {
  const max = template.maxInlineCodes ?? 3
  const valid = template.codes
    .map((c) => (c == null ? '' : String(c).trim()))
    .filter((c) => c.length > 0)
  const inline = valid.length > max ? valid.slice(0, max) : valid
  const extra = valid.length > max ? valid.length - max : 0
  const moreText = template.moreText ?? `… và ${extra} mã khác`

  return (
    <Typography
      component="div"
      sx={{ fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.6 }}
    >
      {template.prefix ? <>{template.prefix} </> : null}
      {inline.map((code, idx) => (
        <Box
          component="span"
          key={`${code}-${idx}`}
          sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}
        >
          {code}
          {idx < inline.length - 1 ? ', ' : ''}
        </Box>
      ))}
      {extra > 0 ? (
        <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          {` ${moreText}`}
        </Box>
      ) : null}
      {template.suffix ? <>{' '}{template.suffix}</> : null}
    </Typography>
  )
}

export default ConfirmDialog
